/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import {
  Button,
  Icon,
  Popup,
  Progress,
  Tooltip,
  VerticalMenu
} from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import { useTranslation } from '@scuf/localization';
import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import { useFileUploadContext } from '../../../context/FileUploadContext/FileUploadContext';
import descriptors from '../../../locales/en-US/descriptors';
import FileIcon from '../../../images/FileIcon.svg';
import _ from 'lodash';
import { ApiContext } from '../../../context/ApiContext';
import '../MeterDataUploadModal/ChooseDataTypeSection/UpdatedChooseDataTypeThemes.scss';

export interface IUploadFile {
  fileId: string;
  displayFileName: string;
  fileName: string;
  fileType: string;
  fileStatus: string;
  createdDate: string;
  fileStatusKey: FileStatusKey;
  message: string;
  progressPercentage: number;
  baseLineStatus?: number;
}

export type FileStatusKey =
  | 'Error'
  | 'Published'
  | 'ReadyToPublish'
  | 'Uploading'
  | 'Publishing';

export interface IUpdatedDataUploadFileTableProps {
  data: Array<IUploadFile>;
  onRefresh: () => void;
}

const FileDeleteActionMenu = (props: {
  uploadFileRow: IUploadFile;
  handleDelete: (fileId: string) => Promise<void>;
}) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { uploadFileRow, handleDelete } = props;
  const [deleting, setDeleting] = useState<boolean>(false);

  return (
    <Popup
      className="emaas-utility-popup"
      position="bottom right"
      element={<Icon root="common" name="ellipsis-horizontal" size="medium" />}
      on="click"
    >
      <VerticalMenu>
        <VerticalMenu>
          <VerticalMenu.Item
            onClick={async () => {
              setDeleting(true);
              try {
                await handleDelete(uploadFileRow.fileId);
              } catch {
                setDeleting(false);
              }
              setDeleting(false);
            }}
            disabled={
              deleting ||
              ['Uploading', 'Published', 'Publishing'].includes(
                uploadFileRow.fileStatusKey
              )
            }
          >
            {deleting ? t(d.DELETING) : t(d.DELETE)}
          </VerticalMenu.Item>
        </VerticalMenu>
      </VerticalMenu>
    </Popup>
  );
};

const UpdatedDataUploadFileTable = (
  props: IUpdatedDataUploadFileTableProps
) => {
  const d = descriptors.energyManagement;
  const [t, lng] = useTranslation();
  const { data: apiData } = props;
  const [loading, setLoading] = useState(false);
  const { onDeleteMeterDataFile, notifyMessage } = useContext(ApiContext);
  const [state, dispatch] = useFileUploadContext();
  const showModal = () => dispatch(FileUploadAction.showUploadModal());

  const handleDelete = async (fileId: string) => {
    setLoading(true);
    try {
      onDeleteMeterDataFile && (await onDeleteMeterDataFile(fileId));
      notifyMessage && notifyMessage(t(d.FILE_DELETED_SUCCESSFULLY), 'success');
      setLoading(false);
    } catch (error) {
      notifyMessage && notifyMessage(t(d.FILE_DELETEING_ERROR), 'error');
      setLoading(false);
    } finally {
      props.onRefresh();
      setLoading(false);
    }
  };

  const statusRender = (celldata: ICellData) => {
    const uploadFileRow: IUploadFile = celldata.rowData;
    return (
      <>
        {uploadFileRow.fileStatusKey === 'Error' && (
          <Tooltip
            element={
              <span className="file-upload-error-icon  file-upload-status-icon">
                <Icon root="common" name="badge-info" />
              </span>
            }
            content={uploadFileRow.message}
            position="bottom center"
          />
        )}
        {uploadFileRow.fileStatusKey === 'ReadyToPublish' && (
          <span className="file-upload-success-icon file-upload-status-icon">
            <Icon root="common" name="badge-check" />
          </span>
        )}
        <span>
          {uploadFileRow.fileStatusKey === 'Uploading'
            ? `${uploadFileRow.fileStatus}`
            : uploadFileRow.fileStatus}
        </span>
      </>
    );
  };

  const [inProgressRowObj, setInProgressRowObj] = useState<IUploadFile[]>();

  const [tableData, setTableData] = useState<IUploadFile[]>([]);

  // To be removed
  useEffect(() => {
    if (inProgressRowObj && inProgressRowObj.length > 0)
      setTableData([...inProgressRowObj, ...apiData]);
    else setTableData([...apiData]);
  }, [apiData.length, inProgressRowObj]);

  const renderFileDeleteAction = (celldata: ICellData) => {
    const uploadFileRow: IUploadFile = celldata.rowData;
    return (
      <FileDeleteActionMenu
        uploadFileRow={uploadFileRow}
        handleDelete={handleDelete}
      />
    );
  };

  return (
    <>
      <div
        className={
          tableData && tableData.length > 0
            ? 'fileupload-datatable datatable-fixed-height'
            : 'fileupload-datatable'
        }
      >
        <DataTable
          data={tableData}
          search={true}
          searchPlaceholder={t(d.SEARCHFOR)}
        >
          <DataTable.Column
            field="displayFileName"
            header={t(d.NAME)}
            sortable={true}
            className="fileupload-name"
            renderer={(cellData) => (
              <>
                <img src={FileIcon} /> {cellData.value}{' '}
              </>
            )}
            initialWidth="40%"
          />
          <DataTable.Column
            field="createdDate"
            header={t(d.DATEOFUPLOAD)}
            initialWidth="20%"
            sortable={true}
            renderer={(cellData) => (
              <>
                {moment(cellData.value)
                  .locale(lng)
                  .format('DD/MM/YY')}
              </>
            )}
          />
          <DataTable.Column
            className="fileStatusforupdateddata"
            field="fileStatusforupdateddata"
            header={t(d.STATUS)}
            renderer={statusRender}
            sortable={true}
          />
          <DataTable.Column
            className="files-action"
            field=""
            header={t(d.ACTIONS)}
            renderer={renderFileDeleteAction}
          />
        </DataTable>
      </div>
      {tableData.length == 0 && (
        <div className="upload-box">
          <div>
            <div className="align-center">{t(d.NOFILESADDEDHEADER)}</div>
            <div className="align-center">{t(d.NOFILESADDEDMESSAGE)}</div>
            <div className="align-center mt-15">
              <Button
                type="primary"
                content={t(d.UPLOADBTN)}
                className="upload-button"
                onClick={showModal}
              ></Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default UpdatedDataUploadFileTable;
